// @flow
import _ from 'lodash';
import type { Nodes } from 'types/trident-campaigns';
import { actionTypeToTemplateType, userTypeToSegmentType } from 'data/trident-campaigns';
import { nodeTypes } from 'data/trident-types';

export function getHedwigTemplateUrl(type: string, id: string) {
  return `${window.Grab.config.HEDWIG_DASH_URI}/templates/create/${type}/?id=${id}`;
}

export function getSegmentUrl(userType: string, segmentName: string) {
  const parts = _.split(segmentName, '.');
  const hasNamespace = parts.length > 1;

  let namespace = '';
  let id = segmentName;

  if (hasNamespace) {
    namespace = `/${parts[0]}`;
    id = parts[1];
  }

  const segType = _.get(userTypeToSegmentType, userType, userType);
  return `${window.Grab.config.SEGMENTATION_PLATFORM_URI}/segments/${id}/${segType}${namespace}`;
}

// getCampaignElements returns an CampaignElements object
// the object includes all the event types, action types, hedwig templates, segments used in updateMembership action or bulk action, and time events
export function getCampaignElements(nodes: Nodes) {
  const eventTypes = [];
  const actionTypes = [];
  const templates = [];
  const rewardIDs = [];
  const bulkActionSegments = [];
  const updateTargetSegments = [];
  const timeEventMap = {}; // key -> {count, meta}

  _.map(nodes, (node) => {
    if (node.type === nodeTypes.scenarios) {
      eventTypes.push(...getEventTypesFromScenariosNode(node));
    }
    if (node.type === nodeTypes.actions || node.type === nodeTypes.action) {
      // if node type is single action, then simulating it as actions node [journey builder campaign]
      const actionElements = getActionElementsFromActionsNode(
        node.type === nodeTypes.action
          ? { data: [_.get(node, 'data', {})] }
          : node,
      );
      actionTypes.push(...actionElements.types);
      rewardIDs.push(...actionElements.rewardIDs);
      templates.push(...actionElements.templates);
      bulkActionSegments.push(...actionElements.bulkActionSegments);
      updateTargetSegments.push(...actionElements.updateTargetSegments);
    }
    if (node.type === nodeTypes.time || node.type === nodeTypes.schedule) {
      const startTime = _.get(node, 'data.startTime');
      const frequency = _.get(node, 'data.frequency');
      const interval = _.get(node, 'data.interval', 1);
      const endTime = _.get(node, 'data.endTime');
      const key = frequency === 'nr' ? `${startTime}-${frequency}` : `${startTime}-${interval}-${frequency}-${endTime}`;
      if (key in timeEventMap) {
        timeEventMap[key].count += 1;
      } else {
        timeEventMap[key] = {
          meta: { startTime, endTime, frequency, interval },
          count: 1,
        };
      }
    }
    if (node.type === nodeTypes.enumeration) {
      const enumElements = getEnumerationElements(node);
      bulkActionSegments.push(...enumElements.segmentNames);
    }
  });

  return {
    eventTypes: _.uniq(eventTypes),
    actionTypes: _.uniq(actionTypes),
    templates: _.uniqWith(templates, _.isEqual),
    bulkActionSegments: _.uniq(bulkActionSegments),
    updateTargetSegments: _.uniq(updateTargetSegments),
    rewardIDs: _.uniq(rewardIDs),
    timeEvents: _.values(timeEventMap),
  };
}

function getEventTypesFromScenariosNode(node: object) {
  const scenarios = _.get(node, 'data.scenarios', []);
  return _.map(scenarios, (scenario) => {
    return scenario.noun;
  });
}

function getActionElementsFromActionsNode(node: object) {
  const actions = _.get(node, 'data', []);
  const types = [];
  const templates = []; // array of objects {type, ID}
  const bulkActionSegments = [];
  const updateTargetSegments = []; // segments used in updateSegmentMembership
  const rewardIDs = [];
  _.map(actions, (action) => {
    types.push(action.type);
    const templateID = _.get(action, 'meta.messagingPayload.templateID');
    if (templateID) {
      templates.push({
        id: templateID, type: _.get(actionTypeToTemplateType, action.type, ''),
      });
    }
    const updateTargetSeg = _.get(action, 'meta.updateSegMembershipPayload.SegmentName');
    if (updateTargetSeg) {
      updateTargetSegments.push(updateTargetSeg);
    }
    const bulkActionSeg = _.get(action, 'meta.segmentName');
    if (bulkActionSeg) {
      bulkActionSegments.push(bulkActionSeg);
    }
    const rewardID = _.get(action, 'meta.rewardID');
    if (rewardID) {
      rewardIDs.push(rewardID);
    }
  });

  return {
    types,
    templates,
    bulkActionSegments,
    updateTargetSegments,
    rewardIDs,
  };
}

function getEnumerationElements(node: object) {
  const segmentNames = _.get(node, 'data.payload.names', []);
  return {
    segmentNames,
  };
}
